// One event, rendered as a card. Port of dashboard/events/_event_card.php.
//
// The rule that matters here: an event's date and time are NAIVE wall-clock in
// the event's own authoring zone, so they are rendered through
// fmtEventDateTime(date, time, event.timeZone, viewerZone). Never
// `new Date(event.eventDate)` — that reads a local time as UTC and shifts the
// day for most of the world.
//
// The zone suffix is always shown. It is what makes "7:00 PM PDT" on a card
// authored in New York legible rather than looking like a bug.

import {
  durationCountdownText,
  eventType,
  eventTypeUsesEndDate,
  fmtEventSchedule,
  type EventSummary,
  type RsvpCounts,
} from '@msc/shared';
import { el } from '../dom.js';
import { rsvpControl } from './rsvpControl.js';
import { addressLine } from './placeFields.js';

/** "Thu, Jul 16 at 7:00 PM PDT", or just the date for an all-day event. */
export function whenLabel(event: EventSummary, viewerZone: string): string {
  return fmtEventSchedule(event.eventDate, event.startTime, event.timeZone, viewerZone);
}

/** The end of a multi-day event, in the viewer's zone. Empty when there is none. */
export function untilLabel(event: EventSummary, viewerZone: string): string {
  // ⚠ GATED ON THE REGISTRY, not just on the column being populated.
  //
  // `usesEndDate` went false for `fasting` on 2026-09-12: the form stopped
  // asking and every write force-nulls the column. But rows created BEFORE that
  // keep the `end_date` they were saved with, so a legacy fast went on printing
  // "Ends: Fri, Sep 18" -- a group-wide answer to a question the model now says
  // is per-person, and one nothing honours any more: `closeMode` moved to
  // 'manual' in the same change and `eventTypesAutoClose()` is empty, so that
  // date does not even close the event. A displayed promise with no mechanism
  // behind it is worse than a missing row.
  //
  // Gated HERE rather than at the call sites so the event page and every card
  // are covered at once, and so a future type that turns `usesEndDate` off
  // inherits the same handling without anybody remembering to.
  if (!event.endDate || !eventTypeUsesEndDate(event.eventType)) return '';
  return fmtEventSchedule(event.endDate, event.endTime, event.timeZone, viewerZone);
}

/**
 * Open / Closed / Canceled.
 *
 * `closeReason` is only meaningful once `closedAt` is set — the column is NOT
 * NULL with a 'completed' default, so an open event reads as 'completed' too.
 * Checking closedAt first is what keeps an open event from being labelled done.
 */
export function statusBadge(event: EventSummary): HTMLElement | null {
  if (!event.closedAt) return null;
  const canceled = event.closeReason === 'canceled';
  return el(
    'span',
    { class: `badge ${canceled ? 'badge-canceled' : 'badge-closed'}` },
    canceled ? 'Canceled' : 'Closed',
  );
}

export interface EventCardOptions {
  /**
   * Attach inline RSVP controls. Used on the event box, so answering never
   * requires opening the event — that inline round trip is the point.
   * Off for the calendar, where a card is a one-line label.
   */
  rsvp?: boolean;
  /**
   * Show which group the event belongs to, with a link to it.
   *
   * Only for lists that SPAN groups — the home page and the site-wide events
   * view. A group-scoped list already says it in the heading, and repeating it
   * on every card is noise.
   */
  groupLabel?: { groupId: number; name: string };
  /**
   * Render the RSVP control read-only, with this sentence as the reason. For a
   * suspended group, where every write is refused.
   */
  frozenNote?: string;
}

export function eventCard(
  event: EventSummary,
  viewerZone: string,
  opts: EventCardOptions = {},
): HTMLElement {
  const def = eventType(event.eventType);
  const badge = statusBadge(event);

  const heading = el('h3', { class: 'event-card-title' }, [
    el('span', { class: 'event-icon', title: def.label }, def.icon),
    ' ',
    el('a', { href: `/events/${event.eventId}` }, event.title),
  ]);
  if (badge) heading.append(' ', badge);

  const lines: HTMLElement[] = [heading, el('p', { class: 'muted' }, whenLabel(event, viewerZone))];

  if (opts.groupLabel) {
    lines.push(
      el('p', { class: 'muted small' }, [
        el('a', { href: `/groups/${opts.groupLabel.groupId}` }, opts.groupLabel.name),
      ]),
    );
  }

  const until = untilLabel(event, viewerZone);
  if (until) lines.push(el('p', { class: 'muted small' }, `Ends ${until}`));

  // A Duration event's whole point is the countdown. Rendered from the UTC
  // instants the server computed, so no zone maths happens on the client.
  if (def.usesEndDate && event.startsAtUtc) {
    const countdown = durationCountdownText(
      Date.parse(`${event.startsAtUtc.replace(' ', 'T')}Z`),
      event.endsAtUtc ? Date.parse(`${event.endsAtUtc.replace(' ', 'T')}Z`) : null,
      Date.now(),
    );
    if (countdown) lines.push(el('p', { class: 'countdown' }, countdown));
  }

  if (event.location) lines.push(el('p', { class: 'small' }, event.location));
  const place = addressLine(event);
  if (place) lines.push(el('p', { class: 'small muted' }, place));

  if (opts.rsvp) {
    // The control owns the headcount line from here, so the static one below
    // would be a second copy that stops updating on the first RSVP.
    lines.push(
      rsvpControl({
        event,
        counts: countsFromSummary(event),
        viewerZone,
        ...(opts.frozenNote ? { frozenNote: opts.frozenNote } : {}),
      }),
      chatLink(event),
    );
  } else {
    lines.push(el('p', { class: 'muted small' }, headcount(event)));
    if (event.myStatus) {
      lines.push(
        el(
          'p',
          { class: 'small' },
          `You said ${event.myStatus}${event.myConfirmedDayof ? ', confirmed on the day' : ''}`,
        ),
      );
    }
  }

  // Tagged so a host can find every card for one event — the sticky panel and a
  // card in the same stream are two views of one event, and answering in one has
  // to show in the other.
  return el('article', { class: 'card event-card', 'data-event-card': event.eventId }, lines);
}

/**
 * The event-chat link, with an unread badge.
 *
 * The LINK shows for everyone, as it did in PHP: a member who cannot read the
 * thread yet lands on a note telling them to RSVP, which is more use than a
 * missing link. The COUNT only appears when there is one, and the server has
 * already zeroed it for anyone without access — so a badge here never reveals
 * the volume of a thread the viewer is shut out of.
 */
function chatLink(event: EventSummary): HTMLElement {
  const link = el('a', { class: 'chat-link', href: `/events/${event.eventId}/chat` }, '💬 Chat');
  if (event.chatUnread > 0) {
    link.append(
      ' ',
      el(
        'span',
        {
          class: 'chat-unread',
          // The digit alone is meaningless to a screen reader beside the word Chat.
          'aria-label': `${event.chatUnread} unread`,
        },
        String(event.chatUnread),
      ),
    );
  }
  return el('p', { class: 'small' }, [link]);
}

/**
 * Write fresh counts back onto a summary — the inverse of countsFromSummary().
 *
 * ⚠ `confirmedDayof` and `confirmedDate` are COPIED, never derived from
 * `status`. A yes with no confirmation is the exact signal day-of confirmation
 * exists to surface, so inferring one from the other would erase it.
 */
export function applyCountsToSummary(event: EventSummary, counts: RsvpCounts): void {
  event.yesCount = counts.yesCount;
  event.maybeCount = counts.maybeCount;
  event.noCount = counts.noCount;
  event.dayofConfirmed = counts.dayofConfirmed;
  event.myStatus = counts.myRsvp?.status ?? null;
  event.myConfirmedDayof = counts.myRsvp?.confirmedDayof ?? false;
  event.myConfirmedDate = counts.myRsvp?.confirmedDate ?? null;
}

/**
 * The list payload already carries every count, so an event box of ten cards
 * costs zero extra requests. `totalResponses` is derived rather than selected —
 * it is only ever used for display.
 *
 * Exported because the sticky panel needs the same projection. Two projections of
 * one event is how a card and a panel end up disagreeing about a headcount — the
 * same rule `docs/plan-feed-as-home.md` states for the card itself.
 */
export function countsFromSummary(event: EventSummary): RsvpCounts {
  return {
    eventId: event.eventId,
    yesCount: event.yesCount,
    dayofConfirmed: event.dayofConfirmed,
    maybeCount: event.maybeCount,
    noCount: event.noCount,
    totalResponses: event.yesCount + event.maybeCount + event.noCount,
    rsvpMin: event.rsvpMin,
    rsvpMax: event.rsvpMax,
    minMet: event.rsvpMin === null ? null : event.yesCount >= event.rsvpMin,
    isFull: event.rsvpMax !== null && event.yesCount >= event.rsvpMax,
    seatsRemaining:
      event.rsvpMax === null ? null : Math.max(0, event.rsvpMax - event.yesCount),
    myRsvp: event.myStatus
      ? {
          status: event.myStatus,
          confirmedDayof: event.myConfirmedDayof,
          confirmedDate: event.myConfirmedDate,
        }
      : null,
  };
}

/**
 * Advance yes count and day-of confirmed count, side by side.
 *
 * Two numbers, deliberately. They tell two different stories — what people said
 * in advance and what they said on the day — and collapsing them into one would
 * throw away the thing this app exists to do.
 */
function headcount(event: EventSummary): string {
  const parts = [`${event.yesCount} yes`];
  if (event.rsvpMax !== null) parts[0] = `${event.yesCount} of ${event.rsvpMax} yes`;
  if (event.maybeCount > 0) parts.push(`${event.maybeCount} maybe`);
  if (event.dayofConfirmed > 0) parts.push(`${event.dayofConfirmed} confirmed day-of`);
  if (event.rsvpMin !== null) {
    parts.push(event.yesCount >= event.rsvpMin ? `minimum of ${event.rsvpMin} met` : `needs ${event.rsvpMin}`);
  }
  return parts.join(' · ');
}
